import React, { useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikTouched } from 'formik';
import { useSelector } from 'react-redux';
import { IEscrowFileCompleteData } from 'types/order/escrow/IEscrowFileCompleteData';
import { ErrorMessage, Notification, SpacerRow } from 'ui-toolkit';
import { RootStore } from 'store/types';
import { createAchPushAction, createAchPushSlice } from 'store/order/escrow/disbursements/achPushes/createAchPushSlice';
import {
  updateInternalTransferAction,
  updateInternalTransferSlice,
} from 'store/order/escrow/disbursements/internalTransfers/updateInternalTransferSlice';
import {
  createManualCheckAction,
  createManualCheckSlice,
} from 'store/order/escrow/disbursements/manualCheckDisbursements/createManualCheckSlice';
import {
  updateManualCheckAction,
  updateManualCheckSlice,
} from 'store/order/escrow/disbursements/manualCheckDisbursements/updateManualCheckSlice';
import {
  updateWireDisbursementAction,
  updateWireDisbursementSlice,
} from 'store/order/escrow/disbursements/wireDisbursements/updateWireDisbursementSlice';
import {
  createWireDisbursementAsyncAction,
  createWireDisbursementSlice,
} from 'store/order/escrow/disbursements/wireDisbursements/postWireDisbursementSlice';
import { createAchAccountAsync } from 'API/accounting/createAchAccountAsync';
import { getAchAccountsExcludingADAction } from '../../../../../store/order/escrow/achAccounts/getAchAccountsExcludingADSlice';
import {
  createAchDepositAction,
  createAchDepositSlice,
} from '../../../../../store/order/escrow/deposits/achDeposits/createAchDepositSlice';
import {
  updateAchDepositAction,
  updateAchDepositSlice,
} from '../../../../../store/order/escrow/deposits/achDeposits/updateAchDepositSlice';
import {
  createCheckDepositAction,
  createCheckDepositSlice,
} from '../../../../../store/order/escrow/deposits/checkDeposits/createCheckDepositSlice';
import {
  updateCheckDepositAction,
  updateCheckDepositSlice,
} from '../../../../../store/order/escrow/deposits/checkDeposits/updateCheckDepositSlice';
import {
  createWireDepositAction,
  createWireDepositSlice,
} from '../../../../../store/order/escrow/deposits/wireDeposits/createWireDepositSlice';
import {
  updateWireDepositAction,
  updateWireDepositSlice,
} from '../../../../../store/order/escrow/deposits/wireDeposits/updateWireDepositSlice';
import {
  createInternalTransferAction,
  createInternalTransferSlice,
} from '../../../../../store/order/escrow/disbursements/internalTransfers/createInternalTransferSlice';
import { createEditIncomingTransactionsValidationSchema } from '../../../../../validation/order/createEditTransactionsValidationSchema';
import { IFetchDataState } from '../../../../../store/IFetchDataState';
import { generateCreateEditTransactionsInitialValues } from '../../../../../utilities/order/escrow/generateCreateEditTransactionsInitialValues';
import { determineBorrowersSellers } from '../../../../../utilities/order/escrow/determineBorrowersSellers';
import { convertIInternalTransferDataToIInternalTransferPatchData } from '../../../../../types/order/escrow/state/internalTransfers/IInternalTransferPatchData';
import { getNewAndUpdateTransactions } from '../../../../../utilities/order/escrow/getNewAndUpdateTransactions';
import { SpaCreateEditIncomingFormCard } from './SpaCreateEditIncomingFormCard';
import { SpaConfirmEscrowModal } from '../confirmEscrowModal/spa/SpaConfirmEscrowModal';
import { SpaCreateEditOutgoingFormCard } from './SpaCreateEditOutgoingFormCard';
import { IConfirmTransaction } from '../../../../../types/order/escrow/IConfirmTransaction';
import { instanceOfICreateAchAccountData } from '../../../../../utilities/order/escrow/instanceOfIAchAccount';
import {
  convertIACHPullDepositDataToIExpectedAchPullDepositRequestWithId,
  IExpectedAchPullDepositRequestWithId,
} from '../../../../../types/order/escrow/request/IExpectedAchPullDepositRequest';
import {
  convertIAchPushDisbursementDataToIAchPushDisbursementRequestWithId,
  IAchPushDisbursementRequestWithId,
} from '../../../../../types/order/escrow/request/IAchPushDisbursementRequest';
import {
  convertWireDisbursementFormDataToWireDisbursementRequest,
  convertWireDisbursementFormDataToUpdateRequest,
} from '../../../../../types/order/escrow/request/IWireDisbursementRequest';
import { getConfirmModalData } from '../../../../../utilities/order/escrow/getConfirmModalData';
import { convertIManualCheckDisbursementDataToIManualCheckDisbursementPostData } from '../../../../../types/order/escrow/state/manualChecks/IManualCheckDisbursementPostData';
import { updateAchPushAction } from '../../../../../store/order/escrow/disbursements/achPushes/updateAchPushSlice';
import { useAppDispatch } from 'store/store';
import { useGetSectionDataQuery } from 'API/order/fees/FeeApi';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { IParty } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { IOrderDetailsData } from '../../../../../ui-toolkit/orders/interfaces/IOrderDetailsData';

export interface IEscrowCreateEditForm {
  escrowData?: IEscrowFileCompleteData;
  orderId: string;
  order: IOrderDetailsData;
  onTransactionUpdate: () => void;
}

export const SpaCreateEditTransactionsForm: React.FC<IEscrowCreateEditForm> = ({
  escrowData,
  orderId,
  order,
  onTransactionUpdate,
}) => {
  const parties = order.parties;
  const escrowFileId = order?.transaction.escrow_file_id || '';
  const dispatch = useAppDispatch();
  const {
    // incomings
    createAchDeposit,
    updateAchDeposit,
    createCheckDeposit,
    updateCheckDeposit,
    createWireDeposit,
    updateWireDeposit,
  } = useSelector((store: RootStore) => store.order.escrow.deposits);

  const {
    // outgoings
    createWireDisbursement,
    updateWireDisbursement,
    createManualCheck,
    updateManualCheck,
    createInternalTransfer,
    updateInternalTransfer,
    createAchPush,
    updateAchPush,
  } = useSelector((store: RootStore) => store.order.escrow.disbursements);

  const { escrowBalancesHeaderSlice } = useSelector((store: RootStore) => store.order.escrow);

  const fetchDataStates: IFetchDataState<any, void>[] = [
    // incomings
    createAchDeposit,
    updateAchDeposit,
    createCheckDeposit,
    updateCheckDeposit,
    createWireDeposit,
    updateWireDeposit,
    // outgoings
    createWireDisbursement,
    updateWireDisbursement,
    createManualCheck,
    updateManualCheck,
    createInternalTransfer,
    updateInternalTransfer,
    createAchPush,
    updateAchPush,
  ];

  const formError: Error | undefined = fetchDataStates.find(({ fetchError }) => !!fetchError)?.fetchError;
  const formChangeFetching: boolean = fetchDataStates.some(({ fetchStatus }) => fetchStatus.fetching);
  const formChangeSuccess: boolean = fetchDataStates.some(({ fetchStatus }) => fetchStatus.success);

  // GET ach account list for ACH containers
  useEffect(() => {
    dispatch(getAchAccountsExcludingADAction(escrowFileId));
  }, [dispatch, escrowFileId]);

  const { data: feeData } = useGetSectionDataQuery(orderId, { skip: !orderId });
  const initialValues = generateCreateEditTransactionsInitialValues(
    escrowData,
    feeData?.flatMap((section) =>
      section.statementSettlementItems.flatMap((lineItem) =>
        lineItem.statementFees.flatMap((fee) => fee.disbursementIds)
      )
    )
  );

  useEffect(() => {
    if (formChangeSuccess) {
      onTransactionUpdate();
      setTimeout(() => {
        // incomings
        dispatch(createAchDepositSlice.actions.reset({}));
        dispatch(updateAchDepositSlice.actions.reset({}));
        dispatch(createCheckDepositSlice.actions.reset({}));
        dispatch(updateCheckDepositSlice.actions.reset({}));
        dispatch(createWireDepositSlice.actions.reset({}));
        dispatch(updateWireDepositSlice.actions.reset({}));
        // outgoings
        dispatch(createWireDisbursementSlice.actions.reset({}));
        dispatch(updateWireDisbursementSlice.actions.reset({}));
        dispatch(createManualCheckSlice.actions.reset({}));
        dispatch(updateManualCheckSlice.actions.reset({}));
        dispatch(createInternalTransferSlice.actions.reset({}));
        dispatch(updateInternalTransferSlice.actions.reset({}));
        dispatch(createAchPushSlice.actions.reset({}));
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formChangeSuccess]);

  const [confirmedSubmit, setConfirmedSubmit] = useState<boolean>(false);
  const [entriesToConfirm, setEntriesToConfirm] = useState<IConfirmTransaction[]>([]);

  const borrowersSellers: IParty[] = determineBorrowersSellers(parties);

  const onSaveWireDeposits = async (formData: IEscrowFileCompleteData) => {
    const { updateData, newData: newWireData } = getNewAndUpdateTransactions(formData.wireDeposits);
    if (updateData.length) dispatch(updateWireDepositAction(updateData));
    if (newWireData.length)
      dispatch(
        createWireDepositAction({
          escrowFileId,
          newWireData,
        })
      );
  };
  const onSaveCheckDeposits = async (formData: IEscrowFileCompleteData) => {
    const { updateData, newData: newCheckData } = getNewAndUpdateTransactions(formData.checkDeposits);
    if (updateData.length) dispatch(updateCheckDepositAction(updateData));
    if (newCheckData.length)
      dispatch(
        createCheckDepositAction({
          escrowFileId,
          newCheckData,
        })
      );
  };

  const onSaveAchPullDeposits = async (formData: IEscrowFileCompleteData) => {
    const { updateData, newData: newAchData } = getNewAndUpdateTransactions(formData.achPullDeposits);
    if (updateData.length) {
      const requests: IExpectedAchPullDepositRequestWithId[] = updateData.map(
        convertIACHPullDepositDataToIExpectedAchPullDepositRequestWithId
      );
      for (let i = 0; i < updateData.length; i++) {
        let request = requests[i];
        let datum = updateData[i];
        if (instanceOfICreateAchAccountData(datum.account)) {
          const createAccountResponse = await createAchAccountAsync(datum.account);
          request.achAccountId = createAccountResponse.id;
        }
      }
      dispatch(updateAchDepositAction(requests));
    }
    if (newAchData.length) {
      //TODO: this ignores type
      let neueAchData = newAchData as any;
      for (const achPull of neueAchData) {
        if (instanceOfICreateAchAccountData(achPull.account)) {
          const createAccountResponse = await createAchAccountAsync(achPull.account);
          //TODO: all types of achPull have IAchAccount?
          achPull.account.id = createAccountResponse.id;
        }
      }
      await dispatch(
        createAchDepositAction({
          escrowFileId,
          newAchData,
        })
      );
    }
  };

  // outgoing save actions
  const onSaveEscrowPaymentWireDisbursements = async (formData: IEscrowFileCompleteData) => {
    const { updateData, newData: newDisbursementData } = getNewAndUpdateTransactions(formData.wireDisbursements);
    if (updateData.length) {
      const requests = updateData.map((item) =>
        convertWireDisbursementFormDataToUpdateRequest({ ...item, type: eDisbursementType.Wire }, 'v1')
      );
      dispatch(updateWireDisbursementAction(requests));
    }
    if (newDisbursementData.length) {
      const requests = newDisbursementData.map((data) =>
        convertWireDisbursementFormDataToWireDisbursementRequest(data, 'v1')
      );
      dispatch(
        createWireDisbursementAsyncAction({
          escrowFileId,
          newDisbursementData: requests,
        })
      );
    }
  };

  const onSaveAchPushes = async (formData: IEscrowFileCompleteData) => {
    const { updateData, newData } = getNewAndUpdateTransactions(formData.achPushDisbursements);
    if (updateData.length) {
      const requests: IAchPushDisbursementRequestWithId[] = updateData.map(
        convertIAchPushDisbursementDataToIAchPushDisbursementRequestWithId
      );
      for (let i = 0; i < updateData.length; i++) {
        let updateDatum = updateData[i];
        let request = requests[i];
        if (instanceOfICreateAchAccountData(updateDatum.account)) {
          const createAccountResponse = await createAchAccountAsync(updateDatum.account);
          request.achAccountId = createAccountResponse.id;
        }
      }
      dispatch(updateAchPushAction(requests));
    }
    if (newData.length > 0) {
      //TODO this ignores type
      let neueData = newData as any;
      for (const achPush of neueData) {
        if (instanceOfICreateAchAccountData(achPush.account)) {
          const createAccountResponse = await createAchAccountAsync(achPush.account);
          achPush.account.id = createAccountResponse.id;
          achPush.achAccountId = createAccountResponse.id;
        } else {
          if (!achPush.achAccountId) {
            achPush.achAccountId = achPush.account?.id;
          }
        }
      }
      await dispatch(
        createAchPushAction({
          escrowFileId,
          data: neueData,
        })
      );
    }
  };
  const onSaveManualChecks = async (formData: IEscrowFileCompleteData) => {
    const { updateData, newData: newCheckData } = getNewAndUpdateTransactions(formData.manualCheckDisbursements);
    if (updateData.length) dispatch(updateManualCheckAction(updateData));
    if (newCheckData.length)
      dispatch(
        createManualCheckAction({
          escrowFileId,
          newCheckData: newCheckData.map(convertIManualCheckDisbursementDataToIManualCheckDisbursementPostData),
        })
      );
  };
  const onSaveInternalTransfers = async (formData: IEscrowFileCompleteData, depositEscrowBankAccountId: string) => {
    const { updateData, newData: newTransferData } = getNewAndUpdateTransactions(formData.restrictedInternalTransfers);
    if (updateData.length) {
      dispatch(updateInternalTransferAction(updateData.map(convertIInternalTransferDataToIInternalTransferPatchData)));
    }
    if (newTransferData.length) {
      dispatch(
        createInternalTransferAction({
          escrowFileId,
          newTransferData: newTransferData,
          depositEscrowBankAccountId,
        })
      );
    }
  };

  const onCloseConfirmationEscrowModal = () => {
    setEntriesToConfirm([]);
    setConfirmedSubmit(false);
  };

  const onFormSubmit = async (formData: IEscrowFileCompleteData, touched: FormikTouched<IEscrowFileCompleteData>) => {
    // clean up values which weren't touched
    const { touchedFormData, confirmModalData }: ITouchedAndConfirmData = determineTouchedAndConfirmData(
      formData,
      touched
    );

    const entries = getConfirmModalData(confirmModalData);
    if (!confirmedSubmit && entries.length > 0) {
      setEntriesToConfirm(entries);
      setConfirmedSubmit(true);
    } else {
      onCloseConfirmationEscrowModal();
      await onSaveEscrowPaymentWireDisbursements(touchedFormData);
      await onSaveAchPushes(touchedFormData);
      await onSaveManualChecks(touchedFormData);
      await onSaveInternalTransfers(touchedFormData, formData.depositEscrowBankAccount!.id);
      await onSaveAchPullDeposits(touchedFormData);
      await onSaveWireDeposits(touchedFormData);
      await onSaveCheckDeposits(touchedFormData);
    }
  };

  const ref = useRef<any>();

  return (
    <>
      {formChangeSuccess && <Notification type={'success'}>Escrow File successfully updated</Notification>}
      <ErrorMessage error={formError} />
      {initialValues && (
        <Formik
          initialValues={initialValues}
          validationSchema={createEditIncomingTransactionsValidationSchema}
          enableReinitialize={true}
          innerRef={ref}
          onSubmit={() => {}}
        >
          {() => (
            <>
              <Form>
                <SpaCreateEditIncomingFormCard
                  borrowersSellers={borrowersSellers}
                  escrowFileId={escrowFileId}
                  formChangeFetching={formChangeFetching}
                  onTransactionUpdate={onTransactionUpdate}
                  onFormSubmit={onFormSubmit}
                />
                <SpacerRow />
                <SpaCreateEditOutgoingFormCard
                  availableAmountDollars={escrowBalancesHeaderSlice.data.availableAmountDollars}
                  borrowersSellers={borrowersSellers}
                  escrowFileId={escrowFileId}
                  formChangeFetching={formChangeFetching}
                  onFormSubmit={onFormSubmit}
                  onTransactionUpdate={onTransactionUpdate}
                  orderId={orderId}
                />
              </Form>
              {entriesToConfirm && entriesToConfirm.length > 0 && (
                <SpaConfirmEscrowModal
                  onClose={onCloseConfirmationEscrowModal}
                  entriesToConfirm={entriesToConfirm}
                  handleSubmit={onFormSubmit}
                />
              )}
            </>
          )}
        </Formik>
      )}
    </>
  );
};

interface ITouchedAndConfirmData {
  touchedFormData: IEscrowFileCompleteData;
  confirmModalData: IEscrowFileCompleteData;
}

const determineTouchedAndConfirmData = (
  formData: IEscrowFileCompleteData,
  touched: FormikTouched<IEscrowFileCompleteData>
): ITouchedAndConfirmData => {
  const touchedData: IEscrowFileCompleteData = {
    id: '',
    createdAt: '',
    updatedAt: '',
  };
  const confirmData = {
    id: '',
    createdAt: '',
    updatedAt: '',
    wireDisbursements: [],
    achPushDisbursements: [],
  };
  for (const key in formData) {
    const value = formData[key];
    if (Array.isArray(value)) {
      touchedData[key] = value.filter((t: any, index: number) => {
        if (
          key === 'wireDisbursements' &&
          !!touched.wireDisbursements &&
          !!touched.wireDisbursements[index] &&
          !!touched.wireDisbursements[index].accountNumber
        ) {
          // if account number has been touched in this wire disbursemet, add to confirm modal data
          confirmData.wireDisbursements.push(t as never);
        } else if (
          key === 'achPushDisbursements' &&
          !!touched.achPushDisbursements &&
          !!touched.achPushDisbursements[index] &&
          !!touched.achPushDisbursements[index].account &&
          !!touched.achPushDisbursements[index].account.accountNumber
        ) {
          // if account number has been touched in this ach push, add to confirm modal data
          confirmData.achPushDisbursements.push(t as never);
        }
        return touched[key] && touched[key][index];
      });
    }
  }
  return {
    touchedFormData: touchedData,
    confirmModalData: confirmData,
  };
};
